
import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from "react";
import { createPortal } from "react-dom";
import { vendorArtistApi, publicClientApi } from "../../lib/api";
import { Panel, StatusBadge, EmptyState } from "./vendorShared";
import {
    normalizeBooking, normalizeClient, normalizeArtist, normalizeService,
    backendSlotTimes, monthNames
} from "../../utils/vendor/vendorUtils";

const DAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

function getWorkingHoursForDate(dateStr, weeklySchedule) {
    if (!dateStr || !weeklySchedule || weeklySchedule.length === 0) {
        return { startHour: 9, startMinute: 0, endHour: 17, endMinute: 0, isDayOff: false };
    }
    const parts = dateStr.split("-");
    const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    const dayName = DAY_NAMES[date.getDay()];
    const schedule = weeklySchedule.find(
        (s) => s.day_of_week?.toLowerCase() === dayName
    );
    if (!schedule) {
        return { startHour: 9, startMinute: 0, endHour: 17, endMinute: 0, isDayOff: false };
    }
    const startParts = (schedule.start_time || "09:00").split(":");
    const endParts = (schedule.end_time || "17:00").split(":");
    return {
        startHour: parseInt(startParts[0], 10),
        startMinute: parseInt(startParts[1], 10) || 0,
        endHour: parseInt(endParts[0], 10),
        endMinute: parseInt(endParts[1], 10) || 0,
        isDayOff: !!schedule.is_day_off,
    };
}

const isSuccessMessage = (msg) => {
    if (!msg) return false;
    const lower = msg.toLowerCase();
    return lower.includes("success") ||
        lower.includes("saved") ||
        lower.includes("updated") ||
        lower.includes("created") ||
        lower.includes("deleted") ||
        lower.includes("loaded") ||
        lower.includes("sent") ||
        lower.includes("verified");
};

const parseStartHour = (timeStr) => {
    if (!timeStr) return NaN;
    if (timeStr.includes(":") && !timeStr.toLowerCase().includes("am") && !timeStr.toLowerCase().includes("pm")) {
        return parseInt(timeStr.split(":")[0], 10);
    }
    const parts = timeStr.toLowerCase().split("-")[0].trim();
    const match = parts.match(/(\d+):(\d+)\s*(am|pm)/);
    if (match) {
        let hour = parseInt(match[1], 10);
        const period = match[3];
        if (period === "pm" && hour < 12) {
            hour += 12;
        } else if (period === "am" && hour === 12) {
            hour = 0;
        }
        return hour;
    }
    const hourPart = parseInt(timeStr.split(":")[0], 10);
    if (!isNaN(hourPart)) {
        if (timeStr.toLowerCase().includes("pm") && hourPart < 12) {
            return hourPart + 12;
        }
        return hourPart;
    }
    return NaN;
};

function BookingDetailDialog({ booking, onClose, onBookingChanged }) {
    const [bookingActionForm, setBookingActionForm] = useState({
        date: "",
        slotTime: "11:00 am - 12:00 pm",
        note: "",
    });
    const [bookingActionMessage, setBookingActionMessage] = useState("");
    const [isBookingActionSaving, setIsBookingActionSaving] = useState(false);

    const clientName = booking.customer?.name || booking.customer_name || booking.data?.customerName || booking.data?.name || booking.data?.fullName || "Client";
    const clientEmail = booking.customer?.email || booking.data?.email || booking.data?.customerEmail || "Not added";
    const clientPhone = booking.customer?.phone || booking.data?.phone || booking.data?.customerPhone || "Not added";
    const serviceName = booking.service?.name || booking.service_name || booking.data?.serviceName || (booking.services && booking.services[0]?.service_name) || "Service";

    let displayDate = booking.booking_date || booking.date || "Not set";
    if (displayDate instanceof Date) {
        displayDate = displayDate.toISOString().split("T")[0];
    } else if (displayDate.includes("T")) {
        displayDate = displayDate.split("T")[0];
    }

    const displayTime = booking.booking_time || booking.time || "Not set";
    const note = booking.note || booking.data?.note || "No notes added.";

    const handleReschedule = async () => {
        if (!bookingActionForm.date) {
            setBookingActionMessage("Choose a new booking date first.");
            return;
        }
        setIsBookingActionSaving(true);
        setBookingActionMessage("");
        try {
            await vendorArtistApi.rescheduleBooking(booking.id, bookingActionForm.date, bookingActionForm.slotTime);
            if (onBookingChanged) {
                onBookingChanged(booking.id, {
                    booking_date: bookingActionForm.date,
                    booking_time: bookingActionForm.slotTime,
                });
            }
            setBookingActionMessage("Booking rescheduled successfully.");
        } catch (error) {
            setBookingActionMessage(error.message || "Unable to reschedule booking.");
        } finally {
            setIsBookingActionSaving(false);
        }
    };

    const handleCancel = async () => {
        setIsBookingActionSaving(true);
        setBookingActionMessage("");
        try {
            await vendorArtistApi.cancelBooking(booking.id, "artist", bookingActionForm.note);
            if (onBookingChanged) {
                onBookingChanged(booking.id, { status: "cancelled" });
            }
            setBookingActionMessage("Booking cancelled successfully.");
        } catch (error) {
            setBookingActionMessage(error.message || "Unable to cancel booking.");
        } finally {
            setIsBookingActionSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
            <div className="w-full max-w-lg overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-2xl flex flex-col max-h-[90vh]">
                <div className="flex items-start justify-between border-b border-slate-100 px-5 py-4">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">Appointment Details</p>
                        <h4 className="mt-1 text-lg font-extrabold text-[#191E49]">{clientName}</h4>
                    </div>
                    <button type="button" onClick={onClose} className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-50" aria-label="Close">
                        <i className="fa-solid fa-xmark" aria-hidden="true" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
                    {/* Core Details Cards */}
                    <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4 space-y-2 text-xs">
                        <div className="flex justify-between font-extrabold text-slate-900 text-sm border-b border-blue-100/50 pb-2 mb-2">
                            <span>{serviceName}{booking.duration && ` (${booking.duration})`}</span>
                            <StatusBadge tone={booking.status === "Pending" ? "amber" : booking.status === "Completed" ? "slate" : booking.status === "cancelled" ? "slate" : "green"}>{booking.status || "Pending"}</StatusBadge>
                        </div>
                        <p className="flex justify-between">
                            <span className="font-semibold text-slate-500">Date:</span>
                            <span className="font-bold text-slate-800">{displayDate}</span>
                        </p>
                        <p className="flex justify-between">
                            <span className="font-semibold text-slate-500">Time:</span>
                            <span className="font-bold text-slate-800">{displayTime ? displayTime.split("-")[0].trim() : "Not set"}</span>
                        </p>
                        <p className="flex justify-between">
                            <span className="font-semibold text-slate-500">Email:</span>
                            <span className="font-bold text-slate-800">{clientEmail}</span>
                        </p>
                        <p className="flex justify-between">
                            <span className="font-semibold text-slate-500">Phone:</span>
                            <span className="font-bold text-slate-800">{clientPhone}</span>
                        </p>
                    </div>

                    {/* Client Note / Note details */}
                    <div className="rounded-xl bg-slate-50 p-4">
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Client Note</p>
                        <p className="mt-2 text-xs leading-relaxed text-slate-700 whitespace-pre-wrap">{note}</p>
                    </div>


                </div>

                <div className="flex items-center justify-end border-t border-slate-100 bg-slate-50 px-5 py-3">
                    <button type="button" onClick={onClose} className="h-9 rounded-lg border border-slate-300 bg-white px-4 text-xs font-bold text-slate-700 hover:bg-slate-100 transition">
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

function WeeklyAvailabilityEditor({ artistId, weeklySchedule, onSave, isLoading, message }) {
    console.log("Function called: WeeklyAvailabilityEditor");
    const [schedules, setSchedules] = useState([]);

    useEffect(() => {
        if (weeklySchedule && weeklySchedule.length > 0) {
            setSchedules(
                weeklySchedule.map((s) => ({
                    day_of_week: s.day_of_week,
                    is_day_off: s.is_day_off,
                    start_time: s.start_time ? s.start_time.substring(0, 5) : "09:00",
                    end_time: s.end_time ? s.end_time.substring(0, 5) : "17:00",
                }))
            );
        } else {
            const defaultDays = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
            setSchedules(
                defaultDays.map((day) => ({
                    day_of_week: day,
                    is_day_off: true,
                    start_time: "09:00",
                    end_time: "17:00",
                }))
            );
        }
    }, [weeklySchedule]);

    const handleToggleDayOff = (index) => {
        setSchedules((current) =>
            current.map((item, idx) =>
                idx === index ? { ...item, is_day_off: !item.is_day_off } : item
            )
        );
    };

    const handleTimeChange = (index, field, value) => {
        setSchedules((current) =>
            current.map((item, idx) =>
                idx === index ? { ...item, [field]: value } : item
            )
        );
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(schedules);
    };

    const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

    if (!artistId) {
        return (
            <div className="rounded-xl border border-dashed border-blue-200 bg-blue-50/60 p-6 text-center text-sm font-semibold text-[#191E49]">
                Please select or configure an artist to view availability settings.
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {message && (
                <div className={`rounded-xl border px-4 py-3 text-sm font-bold ${isSuccessMessage(message) ? "border-emerald-100 bg-emerald-50 text-emerald-700" : "border-red-100 bg-red-50 text-red-700"}`}>
                    {message}
                </div>
            )}
            <div className="divide-y divide-slate-100">
                {schedules.map((item, index) => (
                    <div key={item.day_of_week} className="flex flex-col gap-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="w-32">
                            <span className="font-extrabold text-slate-900">{capitalize(item.day_of_week)}</span>
                        </div>

                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => handleToggleDayOff(index)}
                                className={`h-6 w-11 rounded-full transition-colors duration-200 p-1 ${!item.is_day_off ? "bg-blue-600" : "bg-slate-300"}`}
                            >
                                <span className={`block h-4 w-4 rounded-full bg-white transition-transform duration-200 ${!item.is_day_off ? "translate-x-5" : "translate-x-0"}`} />
                            </button>
                            <span className="text-sm font-semibold text-slate-700">
                                {item.is_day_off ? "Day Off" : "Working"}
                            </span>
                        </div>

                        <div className="flex items-center gap-2">
                            <input
                                type="time"
                                value={item.start_time}
                                onChange={(e) => handleTimeChange(index, "start_time", e.target.value)}
                                disabled={item.is_day_off}
                                className="h-11 rounded-xl border border-slate-200 px-3 text-sm font-bold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-400"
                            />
                            <span className="text-slate-400 font-semibold">to</span>
                            <input
                                type="time"
                                value={item.end_time}
                                onChange={(e) => handleTimeChange(index, "end_time", e.target.value)}
                                disabled={item.is_day_off}
                                className="h-11 rounded-xl border border-slate-200 px-3 text-sm font-bold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-400"
                            />
                        </div>
                    </div>
                ))}
            </div>

            <div className="pt-4 border-t border-slate-100">
                <button
                    type="submit"
                    disabled={isLoading}
                    className="h-11 rounded-xl bg-blue-600 px-6 text-sm font-bold text-white shadow-lg shadow-blue-100 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
                >
                    {isLoading ? "Saving schedule..." : "Save Weekly Schedule"}
                </button>
            </div>
        </form>
    );
}

function DailyBookingCard({ booking, isExpanded, onToggleExpand, onBookingChanged, refreshDashboard }) {
    const [isSaving, setIsSaving] = useState(false);
    const [actionMessage, setActionMessage] = useState("");

    const clientName = booking.customer_name || booking.customer?.name || booking.data?.customerName || booking.data?.name || booking.data?.fullName || "Client";
    const clientEmail = booking.customer_email || booking.customer?.email || booking.data?.email || booking.data?.customerEmail || "Not added";
    // const clientPhone = booking.customer_phone || booking.customer?.phone || booking.data?.phone || booking.data?.customerPhone || "Not added";
    const serviceName = booking.service_name || booking.service?.name || booking.data?.serviceName || (booking.services && booking.services[0]?.service_name) || "Service";
    const startTime = String(booking.start_time || booking.booking_time || booking.time || "").split("-")[0].trim();
    const duration = booking.duration || "";
    const note = booking.note || booking.data?.note || "No notes added.";

    const rawStatus = (booking.status || "Pending").toLowerCase();
    const isPending = rawStatus === "pending";
    const isConfirmed = rawStatus === "confirmed" || rawStatus === "upcoming";
    const isCancelled = rawStatus === "cancelled" || rawStatus === "canceled";

    let cardClasses = "";
    let iconColor = "text-slate-500";

    if (isPending) {
        cardClasses = isExpanded
            ? "border-l-4 border-l-amber-500 border-y-slate-200 border-r-slate-200 bg-white shadow-md ring-1 ring-amber-500/20"
            : "border-l-4 border-l-amber-500/80 border-y-slate-100 border-r-slate-100 bg-white hover:border-y-slate-200 hover:border-r-slate-200 hover:shadow-sm";
        iconColor = "text-amber-500";
    } else if (isConfirmed) {
        cardClasses = isExpanded
            ? "border-l-4 border-l-emerald-500 border-y-slate-200 border-r-slate-200 bg-white shadow-md ring-1 ring-emerald-500/20"
            : "border-l-4 border-l-emerald-500/80 border-y-slate-100 border-r-slate-100 bg-white hover:border-y-slate-200 hover:border-r-slate-200 hover:shadow-sm";
        iconColor = "text-emerald-500";
    } else if (isCancelled) {
        cardClasses = isExpanded
            ? "border-l-4 border-l-rose-400 border-y-slate-200 border-r-slate-200 bg-white shadow-md ring-1 ring-rose-400/20 opacity-80"
            : "border-l-4 border-l-rose-400/70 border-y-slate-100 border-r-slate-100 bg-white hover:border-y-slate-200 hover:border-r-slate-200 hover:shadow-sm opacity-60";
        iconColor = "text-rose-400";
    } else {
        cardClasses = isExpanded
            ? "border-l-4 border-l-blue-500 border-y-slate-200 border-r-slate-200 bg-white shadow-md ring-1 ring-blue-500/20"
            : "border-l-4 border-l-blue-500/80 border-y-slate-100 border-r-slate-100 bg-white hover:border-y-slate-200 hover:border-r-slate-200 hover:shadow-sm";
        iconColor = "text-blue-500";
    }

    const handleConfirm = async (e) => {
        e.stopPropagation();
        setIsSaving(true);
        setActionMessage("");
        try {
            await vendorArtistApi.confirmBooking(booking.id);
            if (onBookingChanged) {
                onBookingChanged(booking.id, { status: "confirmed" });
            }
            if (refreshDashboard) {
                await refreshDashboard();
            }
            setActionMessage("Booking confirmed!");
        } catch (error) {
            setActionMessage(error.message || "Failed to confirm booking.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = async (e) => {
        e.stopPropagation();
        setIsSaving(true);
        setActionMessage("");
        try {
            await vendorArtistApi.cancelBooking(booking.id, "artist", "");
            if (onBookingChanged) {
                onBookingChanged(booking.id, { status: "cancelled" });
            }
            if (refreshDashboard) {
                await refreshDashboard();
            }
            setActionMessage("Booking cancelled!");
        } catch (error) {
            setActionMessage(error.message || "Failed to cancel booking.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div
            onClick={onToggleExpand}
            className={`flex-1 min-w-[200px] max-w-[260px] rounded-xl border-y border-r transition duration-150 cursor-pointer p-3 ${cardClasses}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-1.5 min-w-0">
                    <div className={`mt-0.5 ${iconColor} text-[10px]`}>
                        <i className="fa-solid fa-user-check" />
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-1 flex-wrap">
                            <span className="text-[11px] font-black text-slate-800">{startTime}</span>
                            <span className="text-slate-300">•</span>
                            <span className="text-[11px] font-bold text-slate-700 truncate">{clientName}</span>
                        </div>
                        <p className="text-[10px] font-semibold text-slate-500 mt-0.5 truncate">{serviceName}</p>
                    </div>
                </div>
                <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded-md shrink-0 ${isPending ? "bg-amber-50 text-amber-600" :
                    isCancelled ? "bg-rose-50 text-rose-600" :
                        "bg-emerald-50 text-emerald-600"
                    }`}>
                    {isPending ? "Pending" : isCancelled ? "Cancelled" : "Confirmed"}
                </span>
            </div>

            {isExpanded && (
                <div className="mt-4 pt-3 border-t border-slate-100 space-y-0 text-xs" onClick={(e) => e.stopPropagation()}>
                    <div className="bg-slate-50/50 rounded-xl p-3 border border-slate-100">
                        <p className="flex justify-between">
                            <span className="font-semibold text-slate-500">Email:</span>
                            <span className="font-bold text-slate-800">{clientEmail}</span>
                        </p>
                    </div>

                    <div className="bg-slate-50/50 rounded-xl p-3 border border-slate-100">
                        <p className="font-bold text-slate-500 uppercase tracking-wider text-[9px]">Client Note</p>
                        <p className="mt-1 text-slate-700 leading-relaxed whitespace-pre-wrap">{note}</p>
                    </div>

                    {actionMessage && (
                        <p className={`font-bold ${actionMessage.includes("confirmed") ? "text-emerald-600" : "text-rose-600"}`}>
                            {actionMessage}
                        </p>
                    )}

                    {!isCancelled && (
                        <div className="flex gap-2 pt-1.5">
                            {isPending ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={handleConfirm}
                                        disabled={isSaving}
                                        className="flex-1 h-9 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold transition disabled:opacity-50 text-[11px]"
                                    >
                                        Confirm
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        disabled={isSaving}
                                        className="flex-1 h-9 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold transition disabled:opacity-50 text-[11px]"
                                    >
                                        Cancel
                                    </button>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    disabled={isSaving}
                                    className="w-full h-9 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold transition disabled:opacity-50 text-[11px]"
                                >
                                    Cancel Booking
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
function DailyAvailabilityCalendar({ artistId, bookings = [], selectedDate, setSelectedDate, onBookingChanged, setView, refreshDashboard }) {
    console.log("Function called: DailyAvailabilityCalendar for artist:", artistId);
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const [expandedBookingId, setExpandedBookingId] = useState(null);

    const getDayBookings = useCallback((dateStr) => {
        return bookings.filter(b => {
            const bDate = (b.booking_date || b.date || "").split("T")[0];
            const bArtistId = b.artist?.id || b.artist_id || b.artistId;
            return bDate === dateStr && String(bArtistId) === String(artistId);
        });
    }, [bookings, artistId]);

    const dayBookings = useMemo(() => getDayBookings(selectedDate), [selectedDate, getDayBookings]);

    const fmt = (dateStr) => {
        const [y, m, d] = dateStr.split("-").map(Number);
        return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    };

    const changeDay = (offset) => {
        const [y, m, d] = selectedDate.split("-").map(Number);
        const nd = new Date(y, m - 1, d + offset);
        setSelectedDate(`${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, '0')}-${String(nd.getDate()).padStart(2, '0')}`);
    };

    const hours = Array.from({ length: 14 }, (_, i) => i + 7); // 7am to 8pm

    const bookingsByHour = useMemo(() => {
        const map = {};
        dayBookings.forEach(b => {
            const startHour = parseStartHour(b.start_time || b.time || b.booking_time);
            if (!isNaN(startHour)) {
                if (!map[startHour]) map[startHour] = [];
                map[startHour].push(b);
            }
        });
        return map;
    }, [dayBookings]);

    return (
        <div className="space-y-4">
            {/* Date navigation */}
            <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                <div className="flex justify-center sm:justify-start sm:w-1/3">
                    {setView && (
                        <button
                            type="button"
                            onClick={() => setView("monthly")}
                            className="flex h-9 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-xs font-bold text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 transition"
                        >
                            <i className="fa-solid fa-arrow-left text-[10px]" />
                            Back to Month
                        </button>
                    )}
                </div>

                <div className="flex items-center justify-center gap-3 sm:w-1/3">
                    <button type="button" onClick={() => changeDay(-1)}
                        className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 transition">
                        <i className="fa-solid fa-chevron-left text-xs" />
                    </button>
                    <div className="text-center min-w-[150px]">
                        <p className="font-extrabold text-[#191E49] text-base leading-tight">{fmt(selectedDate)}</p>
                        <p className="text-xs font-semibold text-slate-500 mt-0.5">
                            {selectedDate === todayStr ? "Today" : ""}
                            {dayBookings.length > 0 ? ` · ${dayBookings.length} booking${dayBookings.length > 1 ? "s" : ""}` : " · No bookings"}
                        </p>
                    </div>
                    <button type="button" onClick={() => changeDay(1)}
                        className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 transition">
                        <i className="fa-solid fa-chevron-right text-xs" />
                    </button>
                </div>

                <div className="flex justify-center sm:w-1/3 sm:justify-end">
                    {selectedDate !== todayStr && (
                        <button
                            type="button"
                            onClick={() => setSelectedDate(todayStr)}
                            className="flex h-9 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-xs font-bold text-blue-700 transition hover:bg-blue-50 hover:border-blue-300 whitespace-nowrap"
                        >
                            Jump to today
                        </button>
                    )}
                </div>
            </div>

            {/* Timeline */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="divide-y divide-slate-100">
                    {hours.map(hour => {
                        const label = hour < 12 ? `${hour}:00 AM` : hour === 12 ? "12:00 PM" : `${hour - 12}:00 PM`;
                        const hourBookings = bookingsByHour[hour] || [];

                        const activeBookings = hourBookings.filter(b => {
                            const s = (b.status || '').toLowerCase();
                            return s !== 'cancelled' && s !== 'canceled';
                        });
                        const hasActiveBooking = activeBookings.length > 0;
                        const hasPendingBooking = activeBookings.some(b => (b.status || '').toLowerCase() === 'pending');

                        let rowBg = "";
                        if (hasPendingBooking) {
                            rowBg = "bg-amber-50/20";
                        } else if (hasActiveBooking) {
                            rowBg = "bg-emerald-50/10";
                        }

                        return (
                            <div key={hour} className={`flex min-h-[64px] gap-4 px-4 py-3 transition duration-150 ${rowBg}`}>
                                <div className="w-20 shrink-0 pt-1">
                                    <span className="text-xs font-bold text-slate-500">{label}</span>
                                </div>
                                <div className="flex flex-1 flex-wrap gap-3 items-start">
                                    {hourBookings.map((b, bi) => (
                                        <DailyBookingCard
                                            key={b.id || bi}
                                            booking={b}
                                            isExpanded={expandedBookingId === b.id}
                                            onToggleExpand={() => setExpandedBookingId(expandedBookingId === b.id ? null : b.id)}
                                            onBookingChanged={onBookingChanged}
                                            refreshDashboard={refreshDashboard}
                                        />
                                    ))}
                                    {hourBookings.length === 0 && (
                                        <span className="text-xs font-semibold text-slate-500 self-center">No bookings</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function WeeklyAvailabilityCalendar({ artistId, bookings = [], selectedDate, setSelectedDate, onBookingChanged, setView }) {
    console.log("Function called: WeeklyAvailabilityCalendar");
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const [startOfWeek, setStartOfWeek] = useState(() => {
        const d = new Date();
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(d.setDate(diff));
    });
    const [selectedBookingForDetail, setSelectedBookingForDetail] = useState(null);

    useEffect(() => {
        if (selectedDate) {
            const d = new Date(selectedDate);
            if (!isNaN(d.getTime())) {
                const day = d.getDay();
                const diff = d.getDate() - day + (day === 0 ? -6 : 1);
                setStartOfWeek(new Date(d.setDate(diff)));
            }
        }
    }, [selectedDate]);

    const weekDays = useMemo(() => {
        const days = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(startOfWeek);
            d.setDate(startOfWeek.getDate() + i);
            const dateString = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            days.push({
                name: d.toLocaleDateString("en-US", { weekday: "short" }),
                label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
                dateString,
                isToday: dateString === todayStr
            });
        }
        return days;
    }, [startOfWeek, todayStr]);

    const changeWeek = (offset) => {
        const newStart = new Date(startOfWeek);
        newStart.setDate(startOfWeek.getDate() + offset * 7);
        setStartOfWeek(newStart);
    };

    const getBookingsForDate = (dateStr) => {
        return bookings.filter(b => {
            const bDate = (b.booking_date || b.date || "").split("T")[0];
            const bArtistId = b.artist_id || b.artistId || b.artist?.id;
            return bDate === dateStr && String(bArtistId) === String(artistId);
        });
    };

    const weekRangeLabel = useMemo(() => {
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        const startLabel = startOfWeek.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        const endLabel = endOfWeek.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
        return `${startLabel} – ${endLabel}`;
    }, [startOfWeek]);

    return (
        <div className="space-y-4">
            {/* Week navigation */}
            <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <button type="button" onClick={() => changeWeek(-1)}
                    className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 transition">
                    <i className="fa-solid fa-chevron-left text-xs" />
                </button>
                <div className="text-center">
                    <p className="font-extrabold text-[#191E49] text-base">{weekRangeLabel}</p>
                    <p className="text-xs font-semibold text-slate-500 mt-0.5">Weekly Schedule</p>
                </div>
                <button type="button" onClick={() => changeWeek(1)}
                    className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 transition">
                    <i className="fa-solid fa-chevron-right text-xs" />
                </button>
            </div>

            {/* Weekly Columns Grid */}
            <div className="overflow-x-auto rounded-[22px] border border-slate-200 bg-white shadow-sm">
                <div className="grid grid-cols-7 min-w-[840px] divide-x divide-slate-100 bg-slate-50/50">
                    {weekDays.map(day => {
                        const dayBookings = getBookingsForDate(day.dateString);
                        return (
                            <div key={day.dateString} className="flex flex-col min-h-[380px]">
                                {/* Header cell */}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelectedDate(day.dateString);
                                        if (setView) setView("daily");
                                    }}
                                    className={`flex flex-col items-center py-3 px-2 border-b border-slate-100 transition hover:bg-blue-50/50 text-center w-full ${day.isToday ? "bg-blue-50/30" : "bg-slate-50"
                                        }`}
                                >
                                    <span className={`text-[10px] font-extrabold uppercase tracking-wider ${day.isToday ? "text-blue-600" : "text-slate-400"}`}>
                                        {day.name}
                                    </span>
                                    <span className={`mt-1 text-sm font-extrabold ${day.isToday ? "text-blue-700 font-black" : "text-slate-700"}`}>
                                        {day.label}
                                    </span>
                                </button>

                                {/* Bookings cell list */}
                                <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[300px]">
                                    {dayBookings.map((b, bi) => {
                                        const client = b.customer?.name || b.customer_name || b.data?.customerName || b.data?.name || b.data?.fullName || "Client";
                                        const service = b.service?.name || b.service_name || b.data?.serviceName || (b.services && b.services[0]?.service_name) || "Service";
                                        const startT = String(b.start_time || b.time || b.booking_time || "").split("-")[0].trim();
                                        return (
                                            <button
                                                type="button"
                                                key={b.id || bi}
                                                onClick={() => setSelectedBookingForDetail(b)}
                                                className="w-full text-left rounded-lg border border-blue-100 bg-blue-50/70 p-2 hover:bg-blue-100/70 transition shadow-sm"
                                            >
                                                <p className="text-[10px] font-black text-slate-800">{startT}</p>
                                                <p className="text-[11px] font-extrabold text-slate-900 truncate mt-0.5">{client}</p>
                                                <p className="text-[9px] font-bold text-slate-500 truncate mt-0.5">{service}{b.duration ? ` (${b.duration})` : ""}</p>
                                            </button>
                                        );
                                    })}
                                    {dayBookings.length === 0 && (
                                        <div className="h-full flex items-center justify-center py-12">
                                            <span className="text-[10px] font-semibold text-slate-300">No bookings</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {selectedBookingForDetail && createPortal(
                <BookingDetailDialog
                    booking={selectedBookingForDetail}
                    onClose={() => setSelectedBookingForDetail(null)}
                    onBookingChanged={onBookingChanged}
                />,
                document.body
            )}
        </div>
    );
}

function MonthlyAvailabilityCalendar({ artistId, bookings = [], selectedDate, setSelectedDate, setView, onBookingChanged, setShowBookingSidebar }) {
    console.log("Function called: MonthlyAvailabilityCalendar for artist:", artistId);
    const today = new Date();
    const [currentYear, setCurrentYear] = useState(today.getFullYear());
    const [currentMonth, setCurrentMonth] = useState(today.getMonth()); // 0-indexed

    const calendarCells = useMemo(() => {
        const cells = [];
        const firstDayIndex = new Date(currentYear, currentMonth, 1).getDay();
        const totalDays = new Date(currentYear, currentMonth + 1, 0).getDate();
        const prevTotalDays = new Date(currentYear, currentMonth, 0).getDate();

        // Previous month padding
        for (let i = firstDayIndex - 1; i >= 0; i--) {
            const day = prevTotalDays - i;
            const m = currentMonth === 0 ? 11 : currentMonth - 1;
            const y = currentMonth === 0 ? currentYear - 1 : currentYear;
            const dateString = `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            cells.push({ day, month: m, year: y, isCurrentMonth: false, dateString });
        }
        // Current month days
        for (let day = 1; day <= totalDays; day++) {
            const dateString = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            cells.push({ day, month: currentMonth, year: currentYear, isCurrentMonth: true, dateString });
        }
        // Next month padding
        const remaining = 42 - cells.length;
        for (let day = 1; day <= remaining; day++) {
            const m = currentMonth === 11 ? 0 : currentMonth + 1;
            const y = currentMonth === 11 ? currentYear + 1 : currentYear;
            const dateString = `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            cells.push({ day, month: m, year: y, isCurrentMonth: false, dateString });
        }
        return cells;
    }, [currentYear, currentMonth]);

    const handlePrevMonth = () => {
        setCurrentMonth(prev => {
            if (prev === 0) {
                setCurrentYear(y => y - 1);
                return 11;
            }
            return prev - 1;
        });
    };

    const handleNextMonth = () => {
        setCurrentMonth(prev => {
            if (prev === 11) {
                setCurrentYear(y => y + 1);
                return 0;
            }
            return prev + 1;
        });
    };

    const chooseCalendarDate = (cell) => {
        if (!cell.isCurrentMonth) return;
        setSelectedDate(cell.dateString);
        setView("daily");
    };

    const getBookingsForCell = useCallback((dateString) => {
        return bookings.filter(b => {
            const bDate = b.booking_date || b.date;
            if (!bDate) return false;
            const formattedBDate = bDate.split('T')[0];
            const bArtistId = b.artist?.id || b.artist_id || b.artistId;
            return formattedBDate === dateString && String(bArtistId) === String(artistId);
        });
    }, [bookings, artistId]);

    const formattedSelectedDate = useMemo(() => {
        if (!selectedDate) return "";
        const parts = selectedDate.split('-');
        if (parts.length !== 3) return selectedDate;
        const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
        return date.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    }, [selectedDate]);

    const selectedDayBookings = useMemo(() => {
        return getBookingsForCell(selectedDate);
    }, [selectedDate, getBookingsForCell]);

    return (
        <div className="space-y-4">
            <div className="overflow-hidden rounded-[22px] border border-slate-300 bg-white shadow-[0_18px_55px_rgba(15,23,42,0.08)]">
                <div className="flex flex-col gap-3 border-b border-slate-300 bg-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap items-center gap-6">
                        <div className="flex items-center gap-3">
                            <button type="button" onClick={handlePrevMonth} className="grid h-9 w-9 place-items-center rounded-xl border border-slate-300 bg-white text-slate-600 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700" aria-label="Previous month">
                                <i className="fa-solid fa-chevron-left text-xs" aria-hidden="true" />
                            </button>
                            <div className="min-w-[130px] text-center sm:text-left">
                                <h3 className="text-xl font-extrabold text-[#191E49] leading-tight">{monthNames[currentMonth]} {currentYear}</h3>
                                <p className="text-[10px] font-semibold text-slate-400 mt-0.5 uppercase tracking-wider">Monthly schedule overview</p>
                            </div>
                            <button type="button" onClick={handleNextMonth} className="grid h-9 w-9 place-items-center rounded-xl border border-slate-300 bg-white text-slate-600 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700" aria-label="Next month">
                                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                            </button>
                        </div>


                    </div>
                    {/* <button type="button" onClick={() => setView("daily")} className="h-10 rounded-xl bg-blue-600 px-4 text-sm font-bold text-white shadow-lg shadow-blue-100 transition hover:bg-blue-700">
                        Inspect daily timeline
                    </button> */}
                </div>

                <div className="overflow-x-auto">
                    <div className="min-w-[820px]">
                        <div className="grid grid-cols-7 gap-3 border-b border-slate-300 bg-slate-50 p-3">
                            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                                <div key={day} className="rounded-xl border border-slate-200 bg-white/80 px-3 py-2.5 text-center text-xs font-extrabold uppercase tracking-[0.14em] text-slate-600 shadow-sm">
                                    {day}
                                </div>
                            ))}
                        </div>

                        <div className="grid grid-cols-7 gap-3 bg-slate-100/70 p-3">
                            {calendarCells.map((cell, index) => {
                                const { day, isCurrentMonth, dateString } = cell;
                                const active = selectedDate === dateString && isCurrentMonth;
                                const cellBookings = getBookingsForCell(dateString);

                                const confirmedCount = cellBookings.filter(b => {
                                    const s = (b.status || '').toLowerCase();
                                    return s === 'confirmed' || s === 'upcoming';
                                }).length;
                                const pendingCount = cellBookings.filter(b => {
                                    const s = (b.status || '').toLowerCase();
                                    return s === 'pending' || !s;
                                }).length;
                                const cancelledCount = cellBookings.filter(b => {
                                    const s = (b.status || '').toLowerCase();
                                    return s === 'cancelled' || s === 'canceled';
                                }).length;

                                const hasPending = pendingCount > 0;
                                const hasConfirmed = confirmedCount > 0;
                                const hasCancelled = cancelledCount > 0 && confirmedCount === 0 && pendingCount === 0;

                                let borderStyle = "border-slate-200 bg-white";
                                if (hasPending) {
                                    borderStyle = "border-amber-300 bg-amber-50/10 hover:border-amber-400 hover:bg-amber-50/20";
                                } else if (hasConfirmed) {
                                    borderStyle = "border-emerald-200 bg-emerald-50/5 hover:border-emerald-300 hover:bg-emerald-50/10";
                                } else if (hasCancelled) {
                                    borderStyle = "border-rose-100 bg-rose-50/5 hover:border-rose-200 hover:bg-rose-50/10";
                                }

                                return (
                                    <div
                                        key={`${index}-${day}`}
                                        onClick={() => isCurrentMonth && chooseCalendarDate(cell)}
                                        className={`group min-h-[128px] rounded-2xl border p-3 text-left shadow-sm transition duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 ${active
                                            ? "border-blue-400 bg-white ring-2 ring-blue-500 shadow-[0_18px_35px_rgba(37,99,235,0.16)]"
                                            : !isCurrentMonth
                                                ? "cursor-default border-dashed border-slate-200 bg-white/45 text-slate-300 opacity-60 shadow-none pointer-events-none"
                                                : `${borderStyle} text-slate-800 hover:-translate-y-1 hover:shadow-[0_18px_35px_rgba(15,23,42,0.12)]`
                                            }`}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <span className={`grid h-8 w-8 place-items-center rounded-full text-sm font-extrabold ${active ? "bg-blue-600 text-white" : "text-slate-900 group-hover:bg-white"}`}>
                                                {day}
                                            </span>
                                        </div>

                                        <div className="mt-2 space-y-1.5">
                                            {confirmedCount > 0 && (
                                                <div className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2 py-1 text-[10px] font-extrabold text-emerald-700 border border-emerald-100">
                                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                                    Confirmed: {confirmedCount}
                                                </div>
                                            )}
                                            {pendingCount > 0 && (
                                                <div className="flex items-center gap-1.5 rounded-lg bg-amber-50 px-2 py-1 text-[10px] font-extrabold text-amber-700 border border-amber-100">
                                                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                                                    Pending: {pendingCount}
                                                </div>
                                            )}
                                            {cancelledCount > 0 && (
                                                <div className="flex items-center gap-1.5 rounded-lg bg-rose-50 px-2 py-1 text-[10px] font-extrabold text-rose-700 border border-rose-100">
                                                    <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                                                    Cancelled: {cancelledCount}
                                                </div>
                                            )}
                                            {isCurrentMonth && cellBookings.length === 0 && (
                                                <p className="text-[10px] font-bold text-slate-400 pl-1">
                                                    No bookings
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* <div className="flex flex-col gap-3 border-t border-slate-300 bg-slate-50 px-4 py-4 text-sm sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                        <p className="truncate font-bold text-slate-800">
                            {formattedSelectedDate}
                            <span className="ml-2 font-semibold text-slate-500">
                                {selectedDayBookings.length} bookings scheduled
                            </span>
                        </p>
                    </div>
                    <button type="button" onClick={() => setView("daily")} className="h-10 rounded-xl border border-slate-300 bg-white px-4 text-sm font-bold text-slate-700 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700">
                        Inspect daily timeline
                    </button>
                </div> */}
            </div>
        </div>
    );
}

/* ─── Grid Scroll Column (sub-component for grid time picker) ─── */
function GridScrollColumn({ items, selectedValue, onSelect, formatFn, isHighlightCircle, width = 48 }) {
    const scrollRef = useRef(null);
    const ITEM_H = 36;
    const VISIBLE = 7; // show 7 rows, center = selected
    const PAD = ITEM_H * 3; // 3 items of padding top/bottom so selected can center

    const findIdx = useCallback((val) => {
        const exact = items.indexOf(val);
        if (exact >= 0) return exact;
        let best = 0, bestDiff = Infinity;
        items.forEach((item, i) => {
            const numVal = Number(val);
            const numItem = Number(item);
            if (!isNaN(numVal) && !isNaN(numItem)) {
                const diff = Math.abs(numItem - numVal);
                if (diff < bestDiff) { bestDiff = diff; best = i; }
            }
        });
        return best;
    }, [items]);

    // Scroll to selected item on mount and when selectedValue changes
    useEffect(() => {
        const idx = findIdx(selectedValue);
        if (scrollRef.current) {
            const currentIdx = Math.round(scrollRef.current.scrollTop / ITEM_H);
            if (currentIdx !== idx) {
                scrollRef.current.scrollTo({ top: idx * ITEM_H, behavior: 'auto' });
            }
        }
    }, [selectedValue, findIdx]);

    // Update selection when user scrolls and snapping occurs
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        let timeoutId = null;

        const handleScroll = () => {
            if (timeoutId) clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                const scrollTop = el.scrollTop;
                const idx = Math.round(scrollTop / ITEM_H);
                if (idx >= 0 && idx < items.length) {
                    const newItem = items[idx];
                    if (newItem !== selectedValue) {
                        onSelect(newItem);
                    }
                }
            }, 100);
        };

        el.addEventListener('scroll', handleScroll);
        return () => {
            el.removeEventListener('scroll', handleScroll);
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [items, selectedValue, onSelect]);

    const handleClick = (idx) => {
        onSelect(items[idx]);
        if (scrollRef.current) {
            scrollRef.current.scrollTo({ top: idx * ITEM_H, behavior: 'smooth' });
        }
    };

    return (
        <div style={{ height: ITEM_H * VISIBLE, position: 'relative', width, flexShrink: 0 }}>
            <div
                ref={scrollRef}
                className="drum-scroll"
                style={{
                    height: '100%',
                    overflowY: 'auto',
                    scrollSnapType: 'y mandatory',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                }}
            >
                <div style={{ height: PAD }} />
                {items.map((item, i) => {
                    const isSelected = item === selectedValue;
                    return (
                        <div
                            key={item}
                            onClick={() => handleClick(i)}
                            style={{
                                height: ITEM_H,
                                scrollSnapAlign: 'center',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                position: 'relative',
                                userSelect: 'none',
                            }}
                        >
                            {/* Circle highlight on selected hour */}
                            {isSelected && isHighlightCircle && (
                                <div style={{
                                    position: 'absolute',
                                    width: 32, height: 32,
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #0A3FB6, #2FB4FF)',
                                    zIndex: 0,
                                }} />
                            )}
                            <span style={{
                                position: 'relative',
                                zIndex: 1,
                                fontSize: isSelected ? 16 : 14,
                                fontWeight: isSelected ? 700 : 400,
                                color: isSelected
                                    ? (isHighlightCircle ? '#fff' : '#0A3FB6')
                                    : '#94a3b8',
                                transition: 'all 0.15s ease',
                            }}>
                                {formatFn(item)}
                            </span>
                        </div>
                    );
                })}
                <div style={{ height: PAD }} />
            </div>
        </div>
    );
}

/* ─── Modern Time Picker (compact grid style) ─── */
function ModernTimePicker({ value, onChange, bookingDate, workingHours }) {
    const [isOpen, setIsOpen] = useState(false);
    const [draft, setDraft] = useState(null); // holds draft { h12, m, period } while picker is open
    const pickerRef = useRef(null);
    const ROW_H = 36;
    const VISIBLE_ROWS = 7;

    const { startHour, endHour, startMinute, endMinute, isDayOff } = workingHours || {
        startHour: 9, startMinute: 0, endHour: 17, endMinute: 0, isDayOff: false,
    };

    const isToday = useMemo(() => {
        if (!bookingDate) return false;
        const now = new Date();
        const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        return bookingDate === todayStr;
    }, [bookingDate]);

    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    const startTotal = startHour * 60 + startMinute;
    const endTotal = endHour * 60 + endMinute;
    const effectiveStartTotal = isToday ? Math.max(startTotal, currentHour * 60 + currentMinute) : startTotal;

    // Helper to convert 12h representation to absolute minutes of the day (24h clock)
    const get24h = (h12, min, per) => {
        let h = h12;
        if (per === "AM" && h12 === 12) h = 0;
        else if (per === "PM" && h12 !== 12) h = h12 + 12;
        return h * 60 + min;
    };

    // Convert 12h selection back to 24h "HH:mm" for API
    const to24h = (h12, minute, period) => {
        let h24 = h12;
        if (period === "AM" && h12 === 12) h24 = 0;
        else if (period === "PM" && h12 !== 12) h24 = h12 + 12;
        return `${String(h24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
    };

    // Parse 24h value into 12h display components
    const parsed = useMemo(() => {
        const parts = (value || "09:00").split(":");
        let h24 = parseInt(parts[0], 10);
        let m = parseInt(parts[1], 10);
        if (isNaN(h24)) h24 = 9;
        if (isNaN(m)) m = 0;
        const period = h24 >= 12 ? "PM" : "AM";
        let h12 = h24 % 12;
        if (h12 === 0) h12 = 12;
        return { h12, m, period };
    }, [value]);

    // Active display values (draft when open, parsed when closed)
    const active = draft || parsed;

    const baseHours = useMemo(() => [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], []);
    const baseMinutes = useMemo(() => Array.from({ length: 60 }, (_, i) => i), []);

    // Filter available periods
    const periods = useMemo(() => {
        const p = [];
        const hasAM = startHour < 12;
        const hasPM = endHour >= 12;
        if (hasAM) {
            if (!isToday || (currentHour * 60 + currentMinute < 720)) {
                p.push("AM");
            }
        }
        if (hasPM) {
            p.push("PM");
        }
        return p.length > 0 ? p : ["PM"];
    }, [isToday, currentHour, currentMinute, startHour, endHour]);

    // Filter available hours based on active period
    const availableHours = useMemo(() => {
        return baseHours.filter(h => {
            const minM = get24h(h, 0, active.period);
            const maxM = get24h(h, 59, active.period);
            return maxM >= effectiveStartTotal && minM <= endTotal;
        });
    }, [baseHours, active.period, effectiveStartTotal, endTotal]);

    // Filter available minutes based on active hour and period
    const availableMinutes = useMemo(() => {
        return baseMinutes.filter(m => {
            const totalM = get24h(active.h12, m, active.period);
            return totalM >= effectiveStartTotal && totalM <= endTotal;
        });
    }, [baseMinutes, active.h12, active.period, effectiveStartTotal, endTotal]);

    // Auto-adjust draft if lists update while picker is open
    useEffect(() => {
        if (!isOpen || !draft) return;
        let changed = false;
        let newPeriod = draft.period;
        let newH12 = draft.h12;
        let newM = draft.m;

        if (!periods.includes(newPeriod)) {
            newPeriod = periods[0] || "PM";
            changed = true;
        }

        const allowedHours = baseHours.filter(h => {
            const minM = get24h(h, 0, newPeriod);
            const maxM = get24h(h, 59, newPeriod);
            return maxM >= effectiveStartTotal && minM <= endTotal;
        });
        if (allowedHours.length > 0 && !allowedHours.includes(newH12)) {
            newH12 = allowedHours[0];
            changed = true;
        }

        const allowedMinutes = baseMinutes.filter(m => {
            const totalM = get24h(newH12, m, newPeriod);
            return totalM >= effectiveStartTotal && totalM <= endTotal;
        });
        if (allowedMinutes.length > 0 && !allowedMinutes.includes(newM)) {
            newM = allowedMinutes[0];
            changed = true;
        }

        if (changed) {
            setDraft({ h12: newH12, m: newM, period: newPeriod });
        }
    }, [isOpen, effectiveStartTotal, endTotal, draft?.h12, draft?.m, draft?.period, periods, baseHours, baseMinutes]);

    // Auto-adjust parsed value if it falls outside working hours
    useEffect(() => {
        if (isDayOff) return;
        const parsedTotal = get24h(parsed.h12, parsed.m, parsed.period);
        if (parsedTotal < effectiveStartTotal || parsedTotal > endTotal) {
            // Adjust parsed to the nearest next available future time
            let targetTotal = effectiveStartTotal;
            let targetM = targetTotal % 60;
            let targetH = Math.floor(targetTotal / 60);
            targetM = Math.ceil(targetM / 5) * 5;
            if (targetM >= 60) {
                targetM = 0;
                targetH += 1;
            }
            if (targetH > 23) {
                targetH = 23;
                targetM = 55;
            }
            // convert target 24h to 12h components for defaults
            let targetPeriod = targetH >= 12 ? "PM" : "AM";
            let targetH12 = targetH % 12;
            if (targetH12 === 0) targetH12 = 12;
            onChange(to24h(targetH12, targetM, targetPeriod));
        }
    }, [isDayOff, effectiveStartTotal, endTotal, parsed.h12, parsed.m, parsed.period, onChange]);

    // Open picker — initialize draft from current value
    const openPicker = () => {
        setDraft({ ...parsed });
        setIsOpen(true);
    };

    // Close on outside click
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setIsOpen(false);
                setDraft(null);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [isOpen]);

    // Draft updaters
    const selectHour = (h) => setDraft(d => ({ ...d, h12: h }));
    const selectMinute = (m) => setDraft(d => ({ ...d, m }));
    const selectPeriod = (p) => setDraft(d => ({ ...d, period: p }));

    // OK — commit draft to parent
    const handleOk = () => {
        if (draft) {
            onChange(to24h(draft.h12, draft.m, draft.period));
        }
        setIsOpen(false);
        setDraft(null);
    };

    // Cancel — discard draft
    const handleCancel = () => {
        setIsOpen(false);
        setDraft(null);
    };

    // Clear to default
    const handleClear = () => {
        const defaults = { h12: 12, m: 0, period: "AM" };
        setDraft(defaults);
    };

    if (isDayOff) {
        return (
            <div className="mt-2 flex h-10 w-full items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 text-xs font-semibold text-slate-400">
                <i className="fa-regular fa-calendar-xmark mr-1.5 text-[10px]" />
                Day off — not available
            </div>
        );
    }

    if (availableHours.length === 0) {
        return (
            <div className="mt-2 flex h-10 w-full items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 text-xs font-semibold text-slate-400">
                <i className="fa-regular fa-calendar-xmark mr-1.5 text-[10px]" />
                Working hours ended/not available
            </div>
        );
    }

    const displayTime = `${String(active.h12).padStart(2, "0")}:${String(active.m).padStart(2, "0")} ${active.period}`;

    return (
        <div ref={pickerRef} style={{ position: 'relative' }}>
            <style>{`
                .drum-scroll::-webkit-scrollbar {
                    display: none;
                }
            `}</style>
            {/* Trigger button */}
            <button
                type="button"
                onClick={() => isOpen ? handleCancel() : openPicker()}
                className={`h-10 w-full rounded-xl border bg-white px-3 text-xs font-semibold text-slate-700 outline-none transition shadow-sm flex items-center justify-between gap-2 ${isOpen ? 'border-[#0A3FB6] ring-2 ring-blue-100' : 'border-slate-200 hover:border-slate-300'}`}
            >
                <span className="flex items-center gap-2">
                    <i className="fa-regular fa-clock" style={{ color: '#0A3FB6' }} />
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {active.h12}:{String(active.m).padStart(2, "0")} {active.period}
                    </span>
                </span>
                <i className={`fa-solid fa-chevron-down text-[10px] text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
            </button>

            {/* Grid picker dropdown */}
            {isOpen && (
                <div
                    style={{
                        marginTop: 6,
                        borderRadius: 16,
                        border: '1px solid #e2e8f0',
                        background: '#fff',
                        boxShadow: '0 12px 40px rgba(10, 63, 182, 0.10), 0 2px 8px rgba(0,0,0,0.04)',
                        animation: 'drumPickerIn 0.2s ease-out',
                        overflow: 'hidden',
                        width: '100%',
                    }}
                >
                    {/* ── Header input display ── */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 14px',
                        borderBottom: '1px solid #f1f5f9',
                        gap: 8,
                    }}>
                        <i className="fa-regular fa-clock" style={{ color: '#0A3FB6', fontSize: 14 }} />
                        <span style={{
                            flex: 1,
                            fontSize: 14,
                            fontWeight: 600,
                            color: '#1e293b',
                            fontVariantNumeric: 'tabular-nums',
                            letterSpacing: '0.5px',
                        }}>
                            {displayTime}
                        </span>
                        <button
                            type="button"
                            onClick={handleClear}
                            style={{
                                width: 22, height: 22,
                                borderRadius: '50%',
                                border: 'none',
                                background: '#f1f5f9',
                                color: '#94a3b8',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 11,
                                transition: 'all 0.15s ease',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#64748b'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#94a3b8'; }}
                        >
                            <i className="fa-solid fa-xmark" />
                        </button>
                    </div>

                    {/* ── Grid columns ── */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 20,
                        position: 'relative',
                        height: ROW_H * VISIBLE_ROWS,
                    }}>
                        {/* Center highlight bar */}
                        <div style={{
                            position: 'absolute',
                            top: ROW_H * 3,
                            left: 0,
                            right: 0,
                            height: ROW_H,
                            background: 'linear-gradient(90deg, rgba(10,63,182,0.03), rgba(47,180,255,0.05), rgba(10,63,182,0.03))',
                            borderTop: '1px solid rgba(10,63,182,0.08)',
                            borderBottom: '1px solid rgba(10,63,182,0.08)',
                            zIndex: 0,
                            pointerEvents: 'none',
                        }} />

                        {/* Hour column */}
                        <GridScrollColumn
                            items={availableHours}
                            selectedValue={active.h12}
                            onSelect={selectHour}
                            formatFn={(h) => String(h)}
                            isHighlightCircle={true}
                            width={44}
                        />

                        {/* Minute column */}
                        <GridScrollColumn
                            items={availableMinutes}
                            selectedValue={active.m}
                            onSelect={selectMinute}
                            formatFn={(m) => String(m).padStart(2, '0')}
                            isHighlightCircle={false}
                            width={44}
                        />

                        {/* AM / PM column */}
                        <GridScrollColumn
                            items={periods}
                            selectedValue={active.period}
                            onSelect={selectPeriod}
                            formatFn={(p) => p}
                            isHighlightCircle={false}
                            width={48}
                        />
                    </div>

                    {/* ── Footer buttons ── */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 16,
                        padding: '10px 16px',
                        borderTop: '1px solid #f1f5f9',
                    }}>
                        <button
                            type="button"
                            onClick={handleCancel}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: 12,
                                fontWeight: 700,
                                color: '#0A3FB6',
                                letterSpacing: '0.8px',
                                textTransform: 'uppercase',
                                padding: '4px 8px',
                                borderRadius: 6,
                                transition: 'background 0.15s ease',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(10,63,182,0.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                            CANCEL
                        </button>
                        <button
                            type="button"
                            onClick={handleOk}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: 12,
                                fontWeight: 700,
                                color: '#0A3FB6',
                                letterSpacing: '0.8px',
                                textTransform: 'uppercase',
                                padding: '4px 8px',
                                borderRadius: 6,
                                transition: 'background 0.15s ease',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(10,63,182,0.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function BookForClientSidebar({ dashboardData, selectedDate, selectedArtistId, isOpen, onClose, refreshDashboard, inline = false, weeklySchedule }) {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const [bookingDate, setBookingDate] = useState(selectedDate || todayStr);
    const [artistId, setArtistId] = useState(selectedArtistId || "");
    const [serviceId, setServiceId] = useState("");
    const [clientId, setClientId] = useState("");
    const [isNewClient, setIsNewClient] = useState(false);
    const [clientName, setClientName] = useState("");
    const [clientEmail, setClientEmail] = useState("");
    const [clientPhone, setClientPhone] = useState("");
    const [note, setNote] = useState("");
    const [bookingTime, setBookingTime] = useState("09:00");

    const currentWorkingHours = useMemo(
        () => getWorkingHoursForDate(bookingDate, weeklySchedule),
        [bookingDate, weeklySchedule]
    );

    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState("");

    const clients = useMemo(() => {
        return (dashboardData.users || []).map(normalizeClient);
    }, [dashboardData.users]);

    const services = useMemo(() => {
        return (dashboardData.services || []).map(s => normalizeService(s));
    }, [dashboardData.services]);

    const artists = useMemo(() => {
        return (dashboardData.artists || []).map(normalizeArtist);
    }, [dashboardData.artists]);

    useEffect(() => {
        if (!artistId || !bookingDate) return;
        let isActive = true;
        publicClientApi.getBookingSlots(artistId, bookingDate)
            .then(res => {
                if (!isActive) return;
                const fetchedSlots = res.slots || res.data || [];
                if (fetchedSlots.length > 0) {
                    const firstTime = String(fetchedSlots[0].start_time || fetchedSlots[0].startTime || "09:00").slice(0, 5);
                    setBookingTime(firstTime);
                }
            })
            .catch(err => {
                console.error(err);
            });
        return () => {
            isActive = false;
        };
    }, [artistId, bookingDate]);

    // Update form when active artist or date changes
    useEffect(() => {
        if (selectedDate) setBookingDate(selectedDate);
    }, [selectedDate]);

    useEffect(() => {
        if (selectedArtistId) setArtistId(selectedArtistId);
    }, [selectedArtistId]);

    const handleBook = async (e) => {
        e.preventDefault();
        setMessage("");
        if (!artistId) { setMessage("Please select a stylist."); return; }
        if (!serviceId) { setMessage("Please select a service."); return; }
        if (!bookingDate) { setMessage("Please select a date."); return; }
        if (!bookingTime) { setMessage("Please select a time slot."); return; }

        if (isNewClient) {
            if (!clientName.trim()) { setMessage("Please enter the client's name."); return; }
        } else {
            if (!clientId) { setMessage("Please select an existing client."); return; }
        }

        const now = new Date();
        const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        if (bookingDate < todayStr) {
            setMessage("Please select today's date or a future date.");
            return;
        }

        const workingHours = getWorkingHoursForDate(bookingDate, weeklySchedule);
        if (workingHours.isDayOff) {
            setMessage("Cannot book on a day off.");
            return;
        }

        const { startHour, startMinute, endHour, endMinute } = workingHours;
        const timeParts = bookingTime.split(":");
        const selectedH = parseInt(timeParts[0], 10);
        const selectedM = parseInt(timeParts[1], 10) || 0;
        const selectedTotal = selectedH * 60 + selectedM;

        if (selectedTotal < startHour * 60 + startMinute || selectedTotal > endHour * 60 + endMinute) {
            setMessage("Selected time is outside working hours.");
            return;
        }

        if (bookingDate === todayStr) {
            const currentTotal = now.getHours() * 60 + now.getMinutes();
            if (selectedTotal < currentTotal) {
                setMessage("Cannot book a time in the past.");
                return;
            }
        }

        setIsSaving(true);
        try {
            const payload = {
                artistId: Number(artistId),
                slotId: 0,
                bookingDate,
                serviceId: Number(serviceId),
                note,
                bookingTime,
                ...(isNewClient
                    ? { clientName, clientEmail, clientPhone }
                    : { clientId: Number(clientId) }
                )
            };
            const res = await vendorArtistApi.createBooking(payload);
            if (res.success) {
                if (refreshDashboard) {
                    await refreshDashboard();
                }
                setMessage("Booking created successfully!");
                // Clear form after successful creation
                setServiceId("");
                setClientId("");
                setClientName("");
                setClientEmail("");
                setClientPhone("");
                setNote("");

                setTimeout(() => {
                    setMessage("");
                }, 3000);

                if (!inline && onClose) {
                    setTimeout(() => {
                        onClose();
                    }, 1500);
                }
            } else {
                setMessage(res.message || "Failed to create booking.");
            }
        } catch (err) {
            setMessage(err.message || "Failed to create booking.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleClear = () => {
        setServiceId("");
        setClientId("");
        setClientName("");
        setClientEmail("");
        setClientPhone("");
        setNote("");
        setMessage("");
    };

    if (!isOpen && !inline) return null;

    const formContent = (
        <div className={inline
            ? "w-full bg-white text-slate-800 shadow-[0_18px_55px_rgba(15,23,42,0.08)] flex flex-col rounded-[22px] border border-slate-300 font-sans overflow-hidden"
            : "fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white text-slate-800 shadow-2xl flex flex-col h-full transform transition-transform duration-300 ease-in-out border-l border-slate-200 font-sans"}>

            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-[#0A3FB6] to-[#2FB4FF] text-white flex items-center justify-between">
                <h3 className="text-lg font-extrabold tracking-tight">Add booking</h3>
                {!inline && (
                    <button type="button" onClick={onClose} className="text-white/85 hover:text-white transition" aria-label="Close">
                        <i className="fa-solid fa-xmark text-lg" />
                    </button>
                )}
            </div>

            {/* Body Form */}
            <form onSubmit={handleBook} className="flex-1 overflow-y-auto px-6 py-4 space-y-4 font-sans bg-white">
                {message && (
                    <div className={`rounded-xl border px-4 py-3 text-xs font-bold ${message.includes("successfully") ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50/80 text-rose-700"}`}>
                        {message}
                    </div>
                )}

                {/* Date & Time */}
                <div className="flex items-center gap-4 py-1.5">
                    <div className="w-8 shrink-0 flex justify-center text-slate-500">
                        <i className="fa-regular fa-clock text-lg" />
                    </div>
                    <div className="flex-1 flex flex-col gap-2">
                        <input
                            type="date"
                            value={bookingDate}
                            min={todayStr}
                            onChange={(e) => { setBookingDate(e.target.value); }}
                            className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition shadow-sm"
                        />
                        {/* Modern Time Picker */}
                        <ModernTimePicker value={bookingTime} onChange={setBookingTime} bookingDate={bookingDate} workingHours={currentWorkingHours} />
                    </div>
                </div>

                {/* Client Selection */}
                <div className="flex items-start gap-4 py-1.5">
                    <div className="w-8 shrink-0 flex justify-center text-slate-500 mt-2.5">
                        <i className="fa-solid fa-user-group text-base" />
                    </div>
                    <div className="flex-1 space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Customer</span>
                            <div className="flex rounded-lg border border-blue-100 bg-blue-50 p-0.5">
                                <button
                                    type="button"
                                    onClick={() => { setIsNewClient(false); setMessage(""); }}
                                    className={`h-6 rounded-md px-3 text-[10px] font-bold transition ${!isNewClient ? "bg-gradient-to-r from-[#0A3FB6] to-[#2FB4FF] text-white shadow-sm" : "text-slate-500 hover:text-blue-700"}`}
                                >
                                    Existing
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setIsNewClient(true); setMessage(""); }}
                                    className={`h-6 rounded-md px-3 text-[10px] font-bold transition ${isNewClient ? "bg-gradient-to-r from-[#0A3FB6] to-[#2FB4FF] text-white shadow-sm" : "text-slate-500 hover:text-blue-700"}`}
                                >
                                    New
                                </button>
                            </div>
                        </div>

                        {!isNewClient ? (
                            <select
                                value={clientId}
                                onChange={(e) => setClientId(e.target.value)}
                                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition shadow-sm"
                            >
                                <option value="">-- Select Client --</option>
                                {clients.map(c => (
                                    <option key={c.id} value={c.id}>
                                        {c.name} ({c.email || c.phone || "No contact info"})
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <div className="space-y-2">
                                <input
                                    type="text"
                                    value={clientName}
                                    onChange={(e) => setClientName(e.target.value)}
                                    placeholder="Full Name *"
                                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition shadow-sm placeholder-slate-400"
                                />
                                <div className="">
                                    <input
                                        type="email"
                                        value={clientEmail}
                                        onChange={(e) => setClientEmail(e.target.value)}
                                        placeholder="Email Address"
                                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition shadow-sm placeholder-slate-400"
                                    />
                                    {/* <input
                                        type="tel"
                                        value={clientPhone}
                                        onChange={(e) => setClientPhone(e.target.value)}
                                        placeholder="Phone Number"
                                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition shadow-sm placeholder-slate-400"
                                    /> */}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Stylist */}
                {/* <div className="flex items-center gap-4 py-1.5">
                    <div className="w-8 shrink-0 flex justify-center text-slate-500">
                        <i className="fa-solid fa-user-tie text-base" />
                    </div>
                    <div className="flex-1">
                        <select
                            value={artistId}
                            onChange={(e) => { setArtistId(e.target.value); }}
                            className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition shadow-sm"
                        >
                            <option value="">-- Select Stylist / Artist * --</option>
                            {artists.map(a => (
                                <option key={a.id} value={a.id}>{a.name}</option>
                            ))}
                        </select>
                    </div>
                </div> */}

                {/* Service */}
                <div className="flex items-center gap-4 py-1.5">
                    <div className="w-8 shrink-0 flex justify-center text-slate-500">
                        <i className="fa-solid fa-scissors text-base" />
                    </div>
                    <div className="flex-1">
                        <select
                            value={serviceId}
                            onChange={(e) => setServiceId(e.target.value)}
                            className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition shadow-sm"
                        >
                            <option value="">-- Select Service * --</option>
                            {services.map(s => (
                                <option key={s.id} value={s.id}>{s.name} ({s.duration && s.duration !== "Not set" ? `${s.duration} · ` : ""}${s.price})</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Notes */}
                <div className="flex items-start gap-4 py-1.5">
                    <div className="w-8 shrink-0 flex justify-center text-slate-500 mt-2">
                        <i className="fa-solid fa-align-left text-base" />
                    </div>
                    <div className="flex-1">
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Add description or notes (preferences, requested looks...)"
                            rows={4}
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition shadow-sm placeholder-slate-400"
                        />
                    </div>
                </div>
            </form>

            {/* Footer */}
            <div className="flex items-center justify-end px-6 py-4 gap-3 border-t border-slate-200 bg-slate-50/50">
                <button
                    type="button"
                    onClick={inline ? handleClear : onClose}
                    className="text-xs font-bold text-slate-500 hover:text-slate-800 px-4 py-2 rounded-lg transition"
                >
                    {inline ? "Clear" : "Cancel"}
                </button>
                <button
                    type="submit"
                    onClick={handleBook}
                    disabled={isSaving}
                    className="h-10 px-6 rounded-xl bg-gradient-to-r from-[#0A3FB6] to-[#2FB4FF] text-white text-xs font-extrabold shadow-md shadow-blue-100 hover:shadow-blue-200 hover:opacity-95 transition disabled:opacity-50"
                >
                    {isSaving ? "Saving..." : "Save"}
                </button>
            </div>
        </div>
    );

    if (inline) {
        return formContent;
    }

    return createPortal(
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm transition-opacity duration-300"
                onClick={onClose}
            />
            {formContent}
        </>
        , document.body);
}

export function CalendarPanel({
    dashboardData,
    onBookingChanged,
    refreshDashboard,
    selectedDate: propSelectedDate,
    setSelectedDate: propSetSelectedDate,
    view: propView,
    setView: propSetView
}) {
    console.log("Function called: CalendarPanel");
    const [internalView, setInternalView] = useState("monthly");

    const [internalSelectedDate, setInternalSelectedDate] = useState(() => {
        const today = new Date();
        return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    });

    const view = propView !== undefined ? propView : internalView;
    const setView = propSetView !== undefined ? propSetView : setInternalView;

    const selectedDate = propSelectedDate !== undefined ? propSelectedDate : internalSelectedDate;
    const setSelectedDate = propSetSelectedDate !== undefined ? propSetSelectedDate : setInternalSelectedDate;
    const [showWeeklySettingsModal, setShowWeeklySettingsModal] = useState(false);
    const [showBookingSidebar, setShowBookingSidebar] = useState(false);

    const artists = (dashboardData.artists || []).map(normalizeArtist);
    // eslint-disable-next-line no-unused-vars
    const [selectedArtistId, setSelectedArtistId] = useState(() => {
        return artists[0]?.id || "";
    });
    const [weeklySchedule, setWeeklySchedule] = useState([]);
    const [isLoadingWeekly, setIsLoadingWeekly] = useState(false);
    const [weeklyMessage, setWeeklyMessage] = useState("");

    useEffect(() => {
        if (!selectedArtistId) return;

        let isActive = true;
        setIsLoadingWeekly(true);
        setWeeklyMessage("");

        vendorArtistApi.getArtistWeeklySchedule(selectedArtistId)
            .then((response) => {
                if (!isActive) return;
                if (response.success && response.data) {
                    setWeeklySchedule(response.data);
                } else {
                    setWeeklySchedule([]);
                }
            })
            .catch((error) => {
                if (!isActive) return;
                setWeeklyMessage("Failed to load artist's weekly schedule: " + error.message);
            })
            .finally(() => {
                if (!isActive) return;
                setIsLoadingWeekly(false);
            });

        return () => {
            isActive = false;
        };
    }, [selectedArtistId]);

    const handleSaveWeeklySchedule = async (schedulesToUpdate) => {
        setIsLoadingWeekly(true);
        setWeeklyMessage("");
        try {
            await vendorArtistApi.updateArtistWeeklySchedule(selectedArtistId, schedulesToUpdate);
            setWeeklyMessage("Weekly availability updated successfully.");
            const refreshRes = await vendorArtistApi.getArtistWeeklySchedule(selectedArtistId);
            if (refreshRes.success && refreshRes.data) {
                setWeeklySchedule(refreshRes.data);
            }
        } catch (error) {
            setWeeklyMessage(error.message || "Failed to update weekly schedule.");
        } finally {
            setIsLoadingWeekly(false);
        }
    };

    const profile = dashboardData?.profile;
    const isSubscribed = profile?.subscription_plan && profile.subscription_plan.toLowerCase() !== "trial" && profile?.subscription_status === "active";

    return (
        <div className="relative w-full">
            {!isSubscribed && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-slate-950/20 backdrop-blur-[6px] rounded-[22px] p-6 text-center shadow-inner">
                    <div className="mb-4 grid h-16 w-16 place-items-center rounded-2xl bg-white text-blue-600 shadow-xl border border-blue-50/50 animate-bounce">
                        <i className="fa-solid fa-lock text-2xl" />
                    </div>
                    <h3 className="text-xl font-extrabold text-slate-900">Unlock Calendar Booking</h3>
                    <p className="mt-2 max-w-sm text-sm font-semibold text-slate-600 leading-relaxed">
                        Subscribe to let clients book appointments online, manage your availability, and never miss a booking opportunity.
                    </p>
                </div>
            )}
            <div className={!isSubscribed ? "pointer-events-none select-none filter blur-[4px] opacity-40 w-full" : "w-full"}>
                <Panel
                    title="Availability calendar"
                    eyebrow="Schedule"
                    action={
                        <div className="flex flex-wrap items-center gap-3">
                            {/* Visual Legend */}
                            <div className="flex flex-wrap items-center gap-4 text-xs font-bold text-slate-500">
                                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />Confirmed</span>
                                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" />Pending</span>
                                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-rose-500" />Cancelled</span>
                            </div>

                            <button
                                type="button"
                                onClick={() => setShowWeeklySettingsModal(true)}
                                className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-blue-300 hover:text-blue-700"
                                title="Configure Weekly Openings"
                            >
                                <i className="fa-solid fa-gear" aria-hidden="true" />
                            </button>
                        </div>
                    }
                >
                    <div className="grid gap-8 grid-cols-1 xl:grid-cols-[1fr_420px] items-start">
                        <div className="min-w-0 w-full">
                            {view === "daily" ? (
                                <DailyAvailabilityCalendar
                                    artistId={selectedArtistId}
                                    bookings={dashboardData.bookings}
                                    selectedDate={selectedDate}
                                    setSelectedDate={setSelectedDate}
                                    onBookingChanged={onBookingChanged}
                                    setView={setView}
                                    refreshDashboard={refreshDashboard}
                                />
                            ) : (
                                <MonthlyAvailabilityCalendar
                                    artistId={selectedArtistId}
                                    bookings={dashboardData.bookings}
                                    selectedDate={selectedDate}
                                    setSelectedDate={setSelectedDate}
                                    setView={setView}
                                    onBookingChanged={onBookingChanged}
                                    setShowBookingSidebar={setShowBookingSidebar}
                                />
                            )}
                        </div>

                        <div className="w-full max-w-xl xl:max-w-none">
                            <BookForClientSidebar
                                dashboardData={dashboardData}
                                selectedDate={selectedDate}
                                selectedArtistId={selectedArtistId}
                                isOpen={true}
                                inline={true}
                                onClose={null}
                                refreshDashboard={refreshDashboard}
                                weeklySchedule={weeklySchedule}
                            />
                        </div>
                    </div>

                    {showWeeklySettingsModal && createPortal(
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
                            <div className="w-full max-w-2xl overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-2xl flex flex-col max-h-[90vh]">
                                <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">Settings</p>
                                        <h4 className="mt-1 text-xl font-extrabold text-[#191E49]">Weekly Availability Settings</h4>
                                        <p className="mt-1 text-xs font-semibold text-slate-500">Configure your default weekly working hours and day-offs</p>
                                    </div>
                                    <button type="button" onClick={() => setShowWeeklySettingsModal(false)} className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-slate-200 text-slate-500 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900" aria-label="Close settings">
                                        <i className="fa-solid fa-xmark" aria-hidden="true" />
                                    </button>
                                </div>
                                <div className="flex-1 overflow-y-auto px-6 py-6">
                                    <WeeklyAvailabilityEditor
                                        artistId={selectedArtistId}
                                        weeklySchedule={weeklySchedule}
                                        onSave={handleSaveWeeklySchedule}
                                        isLoading={isLoadingWeekly}
                                        message={weeklyMessage}
                                    />
                                </div>
                                <div className="flex items-center justify-end border-t border-slate-100 bg-slate-50 px-6 py-4">
                                    <button type="button" onClick={() => setShowWeeklySettingsModal(false)} className="h-10 rounded-xl border border-slate-300 bg-white px-5 text-sm font-bold text-slate-700 transition hover:bg-slate-100">
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>,
                        document.body
                    )}
                </Panel>
            </div>
        </div>
    );
}

export function BookingsPanel({ dashboardBookings, onBookingChanged }) {
    console.log("Function called: BookingsPanel");
    const [bookingView, setBookingView] = useState("upcoming");
    const [activeBookingId, setActiveBookingId] = useState(null);
    const [bookingActionForm, setBookingActionForm] = useState({
        date: "",
        slotTime: "11:00 am - 12:00 pm",
        note: "",
    });
    const [bookingActionMessage, setBookingActionMessage] = useState("");
    const [isBookingActionSaving, setIsBookingActionSaving] = useState(false);
    const rows = dashboardBookings.map(normalizeBooking);

    const openBookingActions = (booking) => {
        console.log("Function called: openBookingActions");
        if (!booking.id) {
            setBookingActionMessage("This booking is missing a backend ID.");
            return;
        }

        setActiveBookingId(activeBookingId === booking.id ? null : booking.id);
        setBookingActionMessage("");
        setBookingActionForm({
            date: "",
            slotTime: backendSlotTimes[3],
            note: "",
        });
    };

    const updateBookingActionField = (field) => (event) => {
        setBookingActionForm((current) => ({ ...current, [field]: event.target.value }));
    };

    const rescheduleBooking = async (bookingId) => {
        console.log("Function called: rescheduleBooking");
        if (!bookingActionForm.date) {
            setBookingActionMessage("Choose a new booking date first.");
            return;
        }

        setIsBookingActionSaving(true);
        setBookingActionMessage("");

        try {
            console.log("Executing try block...");
            await vendorArtistApi.rescheduleBooking(bookingId, bookingActionForm.date, bookingActionForm.slotTime);
            onBookingChanged(bookingId, {
                booking_date: bookingActionForm.date,
                booking_time: bookingActionForm.slotTime,
            });
            setBookingActionMessage("Booking rescheduled in backend.");
        } catch (error) {
            console.error("Error caught:", error);
            setBookingActionMessage(error.message || "Unable to reschedule booking.");
        } finally {
            setIsBookingActionSaving(false);
        }
    };

    const cancelBooking = async (bookingId) => {
        console.log("Function called: cancelBooking");

        setIsBookingActionSaving(true);
        setBookingActionMessage("");

        try {
            console.log("Executing try block...");
            await vendorArtistApi.cancelBooking(bookingId, "artist", bookingActionForm.note);
            onBookingChanged(bookingId, { status: "cancelled" });
            setBookingActionMessage("Booking cancelled in backend.");
        } catch (error) {
            console.error("Error caught:", error);
            setBookingActionMessage(error.message || "Unable to cancel booking.");
        } finally {
            setIsBookingActionSaving(false);
        }
    };

    return (
        <Panel
            title="Booking management"
            eyebrow="Bookings"
            action={
                <div className="inline-flex rounded-lg border border-blue-100 bg-blue-50 p-1">
                    {["upcoming", "past"].map((item) => (
                        <button
                            type="button"
                            key={item}
                            onClick={() => setBookingView(item)}
                            className={`h-8 rounded-md px-3 text-xs font-bold capitalize transition ${bookingView === item ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-blue-700"
                                }`}
                        >
                            {item}
                        </button>
                    ))}
                </div>
            }
        >
            {rows.length > 0 ? (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] text-left text-sm">
                        <thead>
                            <tr className="border-b border-slate-100 text-xs uppercase tracking-[0.16em] text-slate-500">
                                <th className="pb-3 font-bold">Client</th>
                                <th className="pb-3 font-bold">Service</th>
                                <th className="pb-3 font-bold">Date</th>
                                <th className="pb-3 font-bold">Time</th>
                                <th className="pb-3 font-bold">Status</th>
                                <th className="pb-3 text-right font-bold">Manage</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((booking) => (
                                <Fragment key={`${booking.id || booking.client}-${booking.service}`}>
                                    <tr key={`${booking.id || booking.client}-${booking.service}`} className="border-b border-slate-100 last:border-0">
                                        <td className="py-4 font-bold text-slate-900">{booking.client}</td>
                                        <td className="py-4 text-slate-600">{booking.service}{booking.duration && ` (${booking.duration})`}</td>
                                        <td className="py-4 text-slate-600">{booking.date}</td>
                                        <td className="py-4 text-slate-600">{booking.time ? booking.time.split("-")[0].trim() : "Not set"}</td>
                                        <td className="py-4">
                                            <StatusBadge tone={booking.status === "Pending" ? "amber" : booking.status === "Completed" ? "slate" : booking.status === "cancelled" ? "slate" : "green"}>{booking.status}</StatusBadge>
                                        </td>
                                        <td className="py-4 text-right">
                                            <button type="button" onClick={() => openBookingActions(booking)} className="rounded-lg border border-blue-200 px-3 py-2 text-xs font-bold text-blue-700 transition hover:bg-blue-50">
                                                Manage
                                            </button>
                                        </td>
                                    </tr>
                                    {activeBookingId === booking.id && (
                                        <tr key={`${booking.id}-actions`} className="border-b border-blue-100 bg-blue-50/50">
                                            <td colSpan="6" className="p-4">
                                                <div className="grid gap-3 md:grid-cols-[160px_180px_1fr_auto_auto]">
                                                    <input
                                                        type="date"
                                                        value={bookingActionForm.date}
                                                        onChange={updateBookingActionField("date")}
                                                        className="h-10 rounded-xl border border-slate-200 px-3 text-sm font-bold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                                                    />
                                                    <select
                                                        value={bookingActionForm.slotTime}
                                                        onChange={updateBookingActionField("slotTime")}
                                                        className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                                                    >
                                                        {backendSlotTimes.map((slot) => (
                                                            <option key={slot} value={slot}>{slot.split("-")[0].trim()}</option>
                                                        ))}
                                                    </select>
                                                    <input
                                                        value={bookingActionForm.note}
                                                        onChange={updateBookingActionField("note")}
                                                        placeholder="Cancel note"
                                                        className="h-10 rounded-xl border border-slate-200 px-3 text-sm font-bold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                                                    />
                                                    <button type="button" onClick={() => rescheduleBooking(booking.id)} disabled={isBookingActionSaving} className="h-10 rounded-xl bg-blue-600 px-4 text-xs font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70">
                                                        Reschedule
                                                    </button>
                                                    <button type="button" onClick={() => cancelBooking(booking.id)} disabled={isBookingActionSaving} className="h-10 rounded-xl border border-red-200 bg-white px-4 text-xs font-bold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70">
                                                        Cancel
                                                    </button>
                                                </div>
                                                {bookingActionMessage && (
                                                    <div className={`mt-3 rounded-xl border px-4 py-3 text-sm font-bold ${isSuccessMessage(bookingActionMessage) ? "border-emerald-100 bg-emerald-50 text-emerald-700" : "border-red-100 bg-red-50 text-red-700"}`}>
                                                        {bookingActionMessage}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <EmptyState title="No bookings found" message="All upcoming appointments booked by clients will be displayed here." />
            )}
        </Panel>
    );
}

export function ClientsPanel({ dashboardUsers, bookings = [] }) {
    console.log("Function called: ClientsPanel");
    const [clientDetails, setClientDetails] = useState({});
    const [clientNotes, setClientNotes] = useState({});
    const [statusOverrides, setStatusOverrides] = useState({});
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [loadingClientId, setLoadingClientId] = useState(null);
    const [savingClientId, setSavingClientId] = useState(null);
    const [isAddingNote, setIsAddingNote] = useState(false);
    const [newNoteText, setNewNoteText] = useState("");
    const [clientMessage, setClientMessage] = useState("");
    const clientRows = dashboardUsers.map(normalizeClient);
    const rows = clientRows.map((client) => {
        const clientBookings = bookings.filter(b =>
            (b.user_id && String(b.user_id) === String(client.backendId)) ||
            (b.vendor_client_id && String(b.vendor_client_id) === String(client.backendId))
        );
        const bookingsCount = clientBookings.length;

        let lastVisit = client.lastVisit;
        if (clientBookings.length > 0) {
            const dates = clientBookings.map(b => new Date(b.booking_date || b.date)).filter(d => !isNaN(d.getTime()));
            if (dates.length > 0) {
                const maxDate = new Date(Math.max(...dates));
                lastVisit = maxDate.toLocaleDateString("en-US", { year: 'numeric', month: 'short', day: 'numeric' });
            }
        }

        let history = "No bookings recorded";
        if (clientBookings.length > 0) {
            const historyItems = clientBookings.slice(0, 3).map(b => {
                const sName = b.services?.[0]?.service_name || b.services?.[0]?.name || "Service";
                const bDate = b.booking_date ? new Date(b.booking_date).toLocaleDateString("en-US", { month: 'short', day: 'numeric' }) : "Date TBD";
                return `${sName} (${bDate})`;
            });
            history = historyItems.join(", ") + (clientBookings.length > 3 ? "..." : "");
        }

        const notesFromBookings = clientBookings.map(b => b.note).filter(Boolean);
        const latestBookingNote = notesFromBookings[0] || "No client notes added yet.";

        return {
            ...client,
            bookings: bookingsCount,
            lastVisit,
            history,
            note: latestBookingNote,
            isActive: statusOverrides[client.backendId] ?? client.isActive,
        };
    });

    const loadClientDetails = async (client) => {
        console.log("Function called: loadClientDetails");
        setClientMessage("");

        if (!client.backendId) {
            setClientMessage("This client is missing a backend ID.");
            return;
        }

        setSelectedClientId(client.backendId);
        setLoadingClientId(client.backendId);
        try {
            console.log("Executing try block...");
            const [detailRes, notesRes] = await Promise.all([
                vendorArtistApi.clients().catch(() => ({ data: [] })),
                vendorArtistApi.getClientNotes(client.backendId),
            ]);
            const clientList = detailRes.data || [];
            const foundClient = clientList.find(c => String(c.id) === String(client.backendId));
            setClientDetails((current) => ({ ...current, [client.backendId]: foundClient || {} }));
            setClientNotes((current) => ({ ...current, [client.backendId]: notesRes.data || [] }));
        } catch (error) {
            console.error("Error caught:", error);
            setClientMessage(error.message || "Unable to load client details.");
        } finally {
            setLoadingClientId(null);
        }
    };

    const handleAddNote = async (clientId) => {
        console.log("Function called: handleAddNote");
        if (!newNoteText.trim()) return;
        setIsAddingNote(true);
        setClientMessage("");
        try {
            console.log("Adding client note...");
            const response = await vendorArtistApi.addClientNote(clientId, newNoteText);
            if (response.success && response.data) {
                setClientNotes((current) => ({
                    ...current,
                    [clientId]: [response.data, ...(current[clientId] || [])],
                }));
                setNewNoteText("");
                setClientMessage("Client note added successfully.");
            }
        } catch (error) {
            console.error("Error adding client note:", error);
            setClientMessage(error.message || "Failed to add client note.");
        } finally {
            setIsAddingNote(false);
        }
    };

    const updateClientStatus = async (client) => {
        console.log("Function called: updateClientStatus");
        setClientMessage("");

        if (!client.backendId) {
            setClientMessage("This client is missing a backend ID.");
            return;
        }

        const nextStatus = client.isActive ? "inactive" : "active";
        setSavingClientId(client.backendId);
        try {
            console.log("Executing try block...");
            const response = await vendorArtistApi.updateClientStatus(client.backendId, nextStatus);
            setStatusOverrides((current) => ({ ...current, [client.backendId]: nextStatus === "active" }));
            setClientMessage(response.message || "Client status updated.");
        } catch (error) {
            console.error("Error caught:", error);
            setClientMessage(error.message || "Unable to update client status.");
        } finally {
            setSavingClientId(null);
        }
    };

    return (
        <div>
            {clientMessage && (
                <div className={`mb-5 rounded-xl border px-4 py-3 text-sm font-bold ${isSuccessMessage(clientMessage) ? "border-emerald-100 bg-emerald-50 text-emerald-700" : "border-red-100 bg-red-50 text-red-700"}`}>
                    {clientMessage}
                </div>
            )}
            {rows.length > 0 ? (
                <div className="grid gap-6 xl:grid-cols-3">
                    {rows.map((client) => {
                        const detail = clientDetails[client.backendId];
                        const notes = clientNotes[client.backendId] || [];

                        return (
                            <section key={client.id || client.name} className="rounded-2xl border border-blue-100 bg-white p-5 shadow-[0_16px_45px_rgba(14,63,182,0.07)]">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <h2 className="text-lg font-extrabold text-[#191E49]">{client.name}</h2>
                                        <p className="mt-1 text-sm text-slate-500">{client.bookings} bookings</p>
                                    </div>
                                    <span className="grid h-10 w-10 place-items-center rounded-full bg-blue-50 text-blue-700">
                                        <i className="fa-regular fa-user" aria-hidden="true" />
                                    </span>
                                </div>
                                <div className="mt-3 space-y-1 text-xs font-semibold text-slate-500">
                                    {client.email && client.email !== "Not added" && (
                                        <p className="flex items-center gap-1.5 truncate">
                                            <i className="fa-regular fa-envelope text-blue-500 w-4 text-center" aria-hidden="true" />
                                            <span>{client.email}</span>
                                        </p>
                                    )}
                                    {/* {client.phone && client.phone !== "Not added" && (
                                        <p className="flex items-center gap-1.5">
                                            <i className="fa-solid fa-phone text-blue-500 w-4 text-center" aria-hidden="true" />
                                            <span>{client.phone}</span>
                                        </p>
                                    )} */}
                                </div>
                                <div className="mt-4">
                                    <StatusBadge tone={client.isActive ? "green" : "slate"}>{client.isActive ? "Active" : "Inactive"}</StatusBadge>
                                </div>
                                <div className="mt-5 rounded-xl bg-slate-50 p-4">
                                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Client note</p>
                                    <p className="mt-2 text-sm leading-6 text-slate-700">{client.note}</p>
                                </div>
                                <div className="mt-4 rounded-xl border border-slate-100 p-4">
                                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Booking history</p>
                                    <p className="mt-2 text-sm leading-6 text-slate-700">{client.history}</p>
                                </div>
                                <div className="mt-5 flex items-center justify-between text-sm">
                                    <span className="text-slate-500">Last visit</span>
                                    <span className="font-bold text-slate-900">{client.lastVisit}</span>
                                </div>
                                {selectedClientId === client.backendId && (
                                    <div className="mt-5 space-y-4">
                                        <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4">
                                            <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">Client Notes</p>
                                            <div className="mt-3 space-y-2 max-h-48 overflow-y-auto pr-1">
                                                {notes.length > 0 ? (
                                                    notes.map((noteItem) => (
                                                        <div key={noteItem.id} className="rounded-lg bg-white p-3 text-xs shadow-sm border border-slate-100">
                                                            <p className="font-semibold text-slate-700 leading-relaxed">{noteItem.note}</p>
                                                            <p className="mt-1.5 text-[9px] text-slate-400 font-bold">
                                                                {new Date(noteItem.created_at).toLocaleString()}
                                                            </p>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <p className="text-xs font-semibold text-slate-500 italic">No notes added yet for this client.</p>
                                                )}
                                            </div>
                                            <div className="mt-4 flex gap-2">
                                                <input
                                                    type="text"
                                                    value={newNoteText}
                                                    onChange={(e) => setNewNoteText(e.target.value)}
                                                    placeholder="Add a client note..."
                                                    className="h-10 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleAddNote(client.backendId)}
                                                    disabled={isAddingNote || !newNoteText.trim()}
                                                    className="h-10 rounded-lg bg-blue-600 px-3 text-xs font-bold text-white transition hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {isAddingNote ? "Adding..." : "Add"}
                                                </button>
                                            </div>
                                        </div>

                                        {detail && (
                                            <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4">
                                                <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">Customer Details</p>
                                                <dl className="mt-3 space-y-2 text-sm">
                                                    <div className="flex justify-between gap-3">
                                                        <dt className="font-semibold text-slate-500">Name</dt>
                                                        <dd className="break-all text-right font-bold text-slate-900">{detail.name}</dd>
                                                    </div>
                                                    <div className="flex justify-between gap-3">
                                                        <dt className="font-semibold text-slate-500">Email</dt>
                                                        <dd className="break-all text-right font-bold text-slate-900">{detail.email || client.email}</dd>
                                                    </div>

                                                    {/* <div className="flex justify-between gap-3">
                                                        <dt className="font-semibold text-slate-500">Phone</dt>
                                                        <dd className="text-right font-bold text-slate-900">{detail.phone || client.phone}</dd>
                                                    </div> */}
                                                    {/* <div className="flex justify-between gap-3">
                                                        <dt className="font-semibold text-slate-500">Gender</dt>
                                                        <dd className="text-right font-bold text-slate-900">{detail.gender || "Not added"}</dd>
                                                    </div>
                                                    <div className="flex justify-between gap-3">
                                                        <dt className="font-semibold text-slate-500">Allergies</dt>
                                                        <dd className="text-right font-bold text-slate-900">{detail.allergies || "Not added"}</dd>
                                                    </div> */}
                                                </dl>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="mt-5 grid gap-2 sm:grid-cols-2">
                                    <button type="button" onClick={() => loadClientDetails(client)} disabled={loadingClientId === client.backendId} className="h-10 rounded-lg border border-blue-200 px-3 text-xs font-bold text-blue-700 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-70">
                                        {loadingClientId === client.backendId ? "Loading..." : "View details"}
                                    </button>
                                    <button type="button" onClick={() => updateClientStatus(client)} disabled={savingClientId === client.backendId} className="h-10 rounded-lg bg-blue-600 px-3 text-xs font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70">
                                        {savingClientId === client.backendId ? "Saving..." : client.isActive ? "Suspend" : "Activate"}
                                    </button>
                                </div>
                            </section>
                        );
                    })}
                </div>
            ) : (
                <EmptyState title="No clients found" message="Registered customers and booking contacts will appear here." />
            )}
        </div>
    );
}

// export function ArtistsPanel({ dashboardArtists, onArtistUpdated }) {
//     console.log("Function called: ArtistsPanel");
//     const artistRows = dashboardArtists.map(normalizeArtist);
//     const [artistDetails, setArtistDetails] = useState({});
//     const [artistServices, setArtistServices] = useState({});
//     const [artistServiceCategory, setArtistServiceCategory] = useState("all");
//     const [selectedArtistId, setSelectedArtistId] = useState(null);
//     const [loadingArtistId, setLoadingArtistId] = useState(null);
//     const [loadingArtistServicesId, setLoadingArtistServicesId] = useState(null);
//     const [savingArtistId, setSavingArtistId] = useState(null);
//     const [artistMessage, setArtistMessage] = useState("");

//     const loadArtistDetails = async (artist) => {
//         console.log("Function called: loadArtistDetails");
//         setArtistMessage("");

//         if (!artist.id) {
//             setArtistMessage("This artist is missing a backend ID.");
//             return;
//         }

//         setSelectedArtistId(artist.id);
//         setLoadingArtistId(artist.id);
//         try {
//             console.log("Executing try block...");
//             const response = await vendorArtistApi.artists();
//             const artistList = response.data || [];
//             const found = artistList.find(a => String(a.id) === String(artist.id)) || {};
//             setArtistDetails((current) => ({ ...current, [artist.id]: found }));
//         } catch (error) {
//             console.error("Error caught:", error);
//             setArtistMessage(error.message || "Unable to load artist details.");
//         } finally {
//             setLoadingArtistId(null);
//         }
//     };

//     const toggleArtistVerify = async (artist) => {
//         console.log("Function called: toggleArtistVerify");
//         setArtistMessage("");
//         setSavingArtistId(artist.id);
//         try {
//             console.log("Executing try block...");
//             // Vendor artists don't have toggle-verify; just update state
//             setArtistMessage("Artist status toggled.");
//         } catch (error) {
//             console.error("Error caught:", error);
//             setArtistMessage(error.message || "Unable to update artist verification.");
//         } finally {
//             setSavingArtistId(null);
//         }
//     };

//     const toggleArtistSuspend = async (artist) => {
//         console.log("Function called: toggleArtistSuspend");
//         setArtistMessage("");
//         setSavingArtistId(artist.id);
//         try {
//             console.log("Executing try block...");
//             // Vendor artists don't have toggle-suspend; just update state
//             setArtistMessage("Artist status toggled.");
//         } catch (error) {
//             console.error("Error caught:", error);
//             setArtistMessage(error.message || "Unable to update artist suspension.");
//         } finally {
//             setSavingArtistId(null);
//         }
//     };

//     const loadArtistServices = async (artist) => {
//         console.log("Function called: loadArtistServices");
//         if (!artist.id) {
//             setArtistMessage("This artist is missing a backend ID.");
//             return;
//         }

//         const category = artistServiceCategory.trim() || "all";
//         setLoadingArtistServicesId(artist.id);
//         setArtistMessage("");
//         try {
//             console.log("Executing try block...");
//             const response = await vendorArtistApi.getArtistServices(artist.id);
//             setArtistServices((current) => ({
//                 ...current,
//                 [artist.id]: response.data || response.services || [],
//             }));
//         } catch (error) {
//             console.error("Error caught:", error);
//             setArtistMessage(error.message || "Unable to load artist services.");
//         } finally {
//             setLoadingArtistServicesId(null);
//         }
//     };

//     return (
//         <Panel title="Artist management" eyebrow="Artists">
//             {artistMessage && (
//                 <div className={`mb-5 rounded-xl border px-4 py-3 text-sm font-bold ${isSuccessMessage(artistMessage) ? "border-emerald-100 bg-emerald-50 text-emerald-700" : "border-red-100 bg-red-50 text-red-700"}`}>
//                     {artistMessage}
//                 </div>
//             )}

//             {artistRows.length > 0 ? (
//                 <div className="grid gap-4 xl:grid-cols-2">
//                     {artistRows.map((artist) => {
//                         const detail = artistDetails[artist.id];

//                         return (
//                             <article key={artist.id || artist.name} className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
//                                 <div className="flex flex-wrap items-start justify-between gap-3">
//                                     <div>
//                                         <h3 className="text-lg font-extrabold text-[#191E49]">{artist.name}</h3>
//                                         <p className="mt-1 text-sm font-semibold text-slate-500">{[artist.city, artist.state].filter(Boolean).join(", ") || artist.category}</p>
//                                     </div>
//                                     <div className="flex flex-wrap gap-2">
//                                         <StatusBadge tone={artist.isVerified ? "green" : "amber"}>{artist.isVerified ? "Verified" : "Unverified"}</StatusBadge>
//                                         <StatusBadge tone={artist.isSuspended ? "slate" : "blue"}>{artist.isSuspended ? "Suspended" : "Active"}</StatusBadge>
//                                     </div>
//                                 </div>
//                                 <div className="mt-4 grid gap-3 sm:grid-cols-2">
//                                     <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
//                                         <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Email</p>
//                                         <p className="mt-1 break-all text-sm font-bold text-slate-900">{artist.email}</p>
//                                     </div>
//                                     <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
//                                         <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Phone</p>
//                                         <p className="mt-1 text-sm font-bold text-slate-900">{artist.phone}</p>
//                                     </div>
//                                 </div>
//                                 {selectedArtistId === artist.id && detail && (
//                                     <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 p-4">
//                                         <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">Artist Profile Details</p>
//                                         <pre className="mt-3 max-h-56 overflow-auto whitespace-pre-wrap rounded-lg bg-white p-3 text-xs font-semibold leading-5 text-slate-700">
//                                             {JSON.stringify(detail, null, 2)}
//                                         </pre>
//                                     </div>
//                                 )}
//                                 {artistServices[artist.id] && (
//                                     <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 p-4">
//                                         <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">Artist services</p>
//                                         {artistServices[artist.id].length > 0 ? (
//                                             <div className="mt-3 space-y-2">
//                                                 {artistServices[artist.id].slice(0, 4).map((service, index) => (
//                                                     <div key={service.id || service.serviceid || index} className="rounded-lg bg-white p-3 text-sm font-semibold text-slate-700">
//                                                         <span className="font-extrabold text-slate-900">{service.name || service.service_name || "Service"}</span>
//                                                         <span className="ml-2 text-slate-500">{service.price ? `$${service.price}` : ""}</span>
//                                                     </div>
//                                                 ))}
//                                             </div>
//                                         ) : (
//                                             <p className="mt-2 text-sm font-semibold text-slate-600">No services returned for this category.</p>
//                                         )}
//                                     </div>
//                                 )}
//                                 <div className="mt-5 grid gap-2 sm:grid-cols-[1fr_auto_auto_auto]">
//                                     <input
//                                         value={artistServiceCategory}
//                                         onChange={(event) => setArtistServiceCategory(event.target.value)}
//                                         placeholder="Service category"
//                                         className="h-10 rounded-lg border border-slate-200 px-3 text-sm font-semibold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
//                                     />
//                                     <button type="button" onClick={() => loadArtistDetails(artist)} disabled={loadingArtistId === artist.id} className="h-10 rounded-lg border border-blue-200 px-3 text-xs font-bold text-blue-700 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-70">
//                                         {loadingArtistId === artist.id ? "Loading..." : "Details"}
//                                     </button>
//                                     <button type="button" onClick={() => loadArtistServices(artist)} disabled={loadingArtistServicesId === artist.id} className="h-10 rounded-lg border border-blue-200 px-3 text-xs font-bold text-blue-700 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-70">
//                                         {loadingArtistServicesId === artist.id ? "Loading..." : "Services"}
//                                     </button>
//                                     <button type="button" onClick={() => toggleArtistVerify(artist)} disabled={savingArtistId === artist.id} className="h-10 rounded-lg bg-blue-600 px-3 text-xs font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70">
//                                         {artist.isVerified ? "Unverify" : "Verify"}
//                                     </button>
//                                     <button type="button" onClick={() => toggleArtistSuspend(artist)} disabled={savingArtistId === artist.id} className="h-10 rounded-lg border border-red-200 bg-white px-3 text-xs font-bold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70">
//                                         {artist.isSuspended ? "Unsuspend" : "Suspend"}
//                                     </button>
//                                 </div>
//                             </article>
//                         );
//                     })}
//                 </div>
//             ) : (
//                 <EmptyState title="No artists found" message="Manage your studio team and individual artist listings here." />
//             )}
//         </Panel>
//     );
// }